import React, { useEffect, useRef } from 'react';

import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../../../../context/AuthContext/AuthContext';
import { useFundedCta } from '../../../../features/application/hooks/useFundedCta';
import { useAffiliationType } from '../../../../features/partner/hooks/useAffiliationType';

import { styled, useTheme } from '@mui/material/styles';

import DrawerHeader from './Header/Header';
import SidebarList from './SidebarList/SidebarList';

import MuiDrawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

import { Box, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import adminItems from '../../Sidebar/adminItems.json';
// The RaiseFX entry ("/switch-rfx", icon RaiseFXIcon, sync chip) is out of items.json until the
// RaiseFX pairing is validated in production; the route stays in the Router for direct access.
import items from '../../Sidebar/items.json';

const drawerWidth = 305;

const SidebarWrapper = styled(MuiDrawer)(() => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
}));

interface SidebarProps {
  open: boolean;
  handleDrawerClose: () => void;
  bannerHeight?: number;
  isRtl?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  open,
  handleDrawerClose,
  bannerHeight = 0,
  isRtl = false,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const fundedCta = useFundedCta();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';
  const location = useLocation();

  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    const currentPathname = location.pathname;

    prevPathnameRef.current = currentPathname;

    if (isMobile && open && prevPathname !== currentPathname) {
      handleDrawerClose();
    }
  }, [location.pathname, isMobile, open, handleDrawerClose]);

  // The partner area is for clients with an affiliation deal only; a plain referrer (the default
  // REFERRAL row every client has) must not see the entry. Hidden until the type is known.
  const isAdminUser = user?.role === 'ROLE_ADMIN';
  const { hasAffiliationDeal } = useAffiliationType(Boolean(user) && !isAdminUser);

  if (!user) return null;

  const isAdmin = user.role === 'ROLE_ADMIN';
  const clientItems = hasAffiliationDeal ? items : items.filter((item) => item.link !== '/partner-area');

  return (
    <SidebarWrapper
      elevation={0}
      variant={isMobile ? 'temporary' : 'permanent'}
      anchor={isRtl ? 'right' : 'left'}
      open={open}
      onClose={handleDrawerClose}
      sx={{
        ...(open && {
          width: drawerWidth,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 0,
            border: 'none',
            top: bannerHeight,
            transition: theme.transitions.create(['left', 'right', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }),
        ...(!open &&
          !isMobile && {
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            width: `calc(${theme.spacing(7)} + 1px)`,
            '& .MuiDrawer-paper': {
              width: `calc(${theme.spacing(10)} + 1px)`,
              backgroundColor: theme.palette.background.paper,
              border: 'none',
              top: bannerHeight,
              transition: theme.transitions.create(['left', 'right', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            },
          }),
      }}
    >
      <DrawerHeader
        open={open}
        handleDrawerClose={handleDrawerClose}
        logoSrc={'/path/to/logo.png'}
        isMobile={isMobile}
        isRtl={isRtl}
      />

      <Divider />

      <Stack
        justifyContent="space-between"
        direction="column"
        sx={{ overflowY: 'scroll', height: '100%', position: 'relative' }}
      >
        {!isAdmin && (
          <Box
            component={Link}
            to={fundedCta.to}
            sx={{
              ...(open ? { display: 'none' } : { display: 'flex' }),
              justifyContent: 'center',
              alignItems: 'center',
              position: 'absolute',
              backgroundColor: theme.palette.primary.main,
              marginTop: '25px',
              borderRadius: '100%',
              padding: '20px',
              height: '20px',
              width: '20px',
              left: '50%',
              right: '50%',
              transform: 'translateX(-50%)',
              textDecoration: 'none',
              color: 'white',
            }}
          >
            <AddIcon />
          </Box>
        )}

        <Box>
          <SidebarList
            items={user.role === 'ROLE_ADMIN' ? adminItems : clientItems}
            open={open}
            isMobile={isMobile}
            handleDrawerClose={handleDrawerClose}
            isDarkMode={isDarkMode}
          />
        </Box>
      </Stack>
    </SidebarWrapper>
  );
};

export default Sidebar;
